import { useQuery } from '@tanstack/react-query';
import { Link, useInRouterContext } from 'react-router-dom';
import { TAB_SLUGS, type ReferenceLinkTargetType } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useMembership } from '../../lib/permissions.js';
import { Icon } from '../Icon.js';
import { referencesKey } from './hooks.js';

/** The library filtered to the references linked to one item. A project id is a valid address. */
export function referencesLinkedPath(projectId: string, type: ReferenceLinkTargetType, id: string): string {
  return `/projects/${projectId}/${TAB_SLUGS.REFERENCES}?linked=${type.toLowerCase()}&linkedId=${encodeURIComponent(id)}`;
}

/**
 * "REFERENCES · n" on a scene and on a place (design §6): how many references
 * are linked to this item, opening the library filtered to them. Shown only
 * when there is at least one — an entry that always says 0 is noise on every
 * panel. A courtesy, not a control: it waits for a KNOWN read permission
 * rather than guessing yes, and renders nothing outside the workspace router.
 */
export function ReferencesCountLink({
  projectId,
  type,
  id,
}: {
  projectId: string;
  type: Extract<ReferenceLinkTargetType, 'SCENE' | 'PLACE'>;
  id: string;
}) {
  const routed = useInRouterContext();
  const membership = useMembership();
  const readable = membership?.permissions.includes('READ_REFERENCES') === true;
  const count = useQuery({
    queryKey: [...referencesKey(projectId), 'count', type, id],
    queryFn: () => api.referenceLibrary(projectId, { linked: type, linkedId: id, limit: 1 }),
    enabled: routed && readable && projectId !== '' && id !== '',
    staleTime: 60_000,
    retry: false,
  });
  const total = count.data?.total ?? 0;
  if (!routed || total === 0) return null;

  return (
    <Link
      to={referencesLinkedPath(projectId, type, id)}
      className="ctl ref-count-link"
      data-testid={`ref-count-${type}-${id}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
    >
      <Icon name="image" size={14} />
      {`References · ${String(total)}`}
    </Link>
  );
}
